/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component} from 'react';
import {Link, Redirect, withRouter} from "react-router-dom";

import {isUserSignedIn, UserContext} from "../UserContext";
import WaitingComponent from "../components/WaitingComponent";
import AccountNavbar from "./AccountNavbar";
import AppFooter from "../components/AppFooter";
import AccountSignedOutEmptyState from "./AccountSignedOutEmptyState";
import {get, handleErrorFetch, post, put} from "../services/APIService";
import {failedToast, successToast} from "../helpers/ToastHelper";
import {ToastContainer} from "react-toastify";
import {FING_ROLE_OWNER, rolePrettyName} from "../helpers/BillingHelper";
import {isPermissionAllowed, isPermissionAllowedForWorkspace} from "../helpers/AccountHelper";

class WorkspaceDashboard extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);

        this.state = {
            showCreateModal: false,
            newWorkspaceName: "",
            accesses: null
        };
    }

    componentDidMount() {
        document.title = "Fing - Workspace dashboard";
        this.fetchWorkspaceAccessList();
    }

    fetchProfile() {
        return fetch('/rest/users/profile')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json());
    }

    fetchWorkspaceAccessList() {
        get("/rest/workspace/access/list")
            .then((accesses) => {
                this.setState({accesses: accesses});
            })
            .catch((e) => {
                console.log('Error in get auth apps: ', e);
                this.setState({accesses: []})
            });
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
    }


    render() {
        return (
            <div>
                <AccountNavbar mode='account' />

                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>

                <AppFooter />
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        if (!userContext || !userContext.userProfileLoaded) {
            return <div className="container space-2">
                <div className="row mt-3">
                    <div className="col-lg-12 text-center">
                        <WaitingComponent className="my-3" />
                    </div>
                </div>
            </div>;
        } else if (isUserSignedIn(userContext) && userContext.stripePublicApiKey) {
            return this.renderMainContentSignedIn(userContext);
        } else {
            return this.renderMainContentPromo();
        }
    }

    renderMainContentSignedIn(userContext) {
        const canCreateWorkspaces = isPermissionAllowed(userContext.userProfile, "create_workspace");
        const onCreate = () => {
            this.setState({showCreateModal: true});
        };

        const {accesses} = this.state;
        if (accesses === null) return '';
        if (!canCreateWorkspaces && accesses.length === 0) {
            return <Redirect to={`/account`}/>;
        }

        return (
            <div className="bg-light">
                <div className="container space-1">

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h3 className="mb-0">Workspace accesses</h3>
                            <p className="text-muted mb-0">Review and manage your access to shared workspaces</p>
                        </div>

                        {
                            canCreateWorkspaces &&
                            <button className="btn btn-sm btn-primary" onClick={onCreate}><i className="fa fa-plus mr-1" />Create</button>
                        }
                    </div>

                    <div className="card card-bordered">
                        <table className="table table-responsive-lg">
                            <thead>
                                <tr>
                                    <th>Workspace</th>
                                    <th>Role</th>
                                    <th>State</th>
                                    <th>Networks</th>
                                    <th>Agents</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody>
                                {accesses && accesses.map((wa,idx) => <tr key={`wa_${idx}`}>
                                    <td>{this.renderWorkspaceName(wa)}</td>
                                    <td>{this.renderRole(wa)}</td>
                                    <td>{this.renderAccessState(wa)}</td>
                                    <td>{this.renderNetworkCount(wa)}</td>
                                    <td>{this.renderAgentCount(wa)}</td>
                                    <td>{this.renderAccessActions(userContext, wa)}</td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>

                {this.renderCreateModal(userContext)}
                <ToastContainer />
            </div>
        );
    }

    renderMainContentPromo() {
        return <AccountSignedOutEmptyState />;
    }

    renderWorkspaceName(wa) {
        return wa.workspaceName;
    }

    renderRole(wa) {
        return rolePrettyName(wa.role);
    }
    renderAccessState(wa) {
        const {role, accessState, joinDate, invitationDate, leaveDate} = wa;
        if (accessState === "GRANTED" && role === FING_ROLE_OWNER)
            return <><span className="fas fa-fw mr-1 fa-check-circle text-green"/>{`Created on ${new Date(joinDate).toLocaleDateString()}`}</>;
        else if (accessState === "GRANTED" && joinDate)
            return <><span className="fas fa-fw mr-1 fa-check-circle text-green"/>{`Joined on ${new Date(joinDate).toLocaleDateString()}`}</>;
        else if (accessState === "PENDING" && invitationDate)
            return <><span className="fas fa-fw mr-1 fa-hourglass text-blue"/>{`Invited on ${new Date(invitationDate).toLocaleDateString()}`}</>;
        else if (accessState === "LEFT" && leaveDate)
            return <><span className="fa fa-fw mr-1 fa-times-circle text-gray"/>{`Left on ${new Date(leaveDate).toLocaleDateString()}`}</>;
        else
            return accessState || "-";
    }

    renderNetworkCount(wa) {
        const {accessState, networkCount} = wa;
        return accessState === "GRANTED" ? networkCount || 0 : "";
    }
    renderAgentCount(wa) {
        const {accessState, agentCount} = wa;
        return accessState === "GRANTED" ? agentCount || 0 : "";
    }

    renderAccessActions(userContext, wa) {
        const onAccept = (event) => {
            event.preventDefault();
            if (!wa || !wa.accessId) return;

            put(`/rest/workspace/access/accept?accessId=${wa.accessId}`)
                .then((updatedWorkspace) => {
                    successToast("Invitation accepted.");
                    this.fetchWorkspaceAccessList();
                })
                .catch((error) => {
                    console.log('Error in accepting invitation: ', error)

                    if (error && error.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    } else if (error && error.code === 403) {
                        failedToast("Token is no more valid.");
                    } else {
                        failedToast("Something went wrong.");
                    }
                });
        };

        const onLeave = (event) => {
            event.preventDefault();

            put(`/rest/workspace/access/leave?accessId=${wa.accessId}`)
                .then((updatedWorkspace) => {
                    successToast(`You left the workspace ${wa.workspaceName}.`);
                    this.fetchWorkspaceAccessList();
                })
                .catch((error) => {
                    failedToast("Something went wrong.");

                    if (error && error.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }

                    console.log('Error in leaving workspace: ', error)
                });
        };

        const {accessState} = wa;
        if (accessState === "PENDING") {
            return <div className="text-right">
                <button onClick={onAccept} className="btn btn-xs btn-primary" disabled={!wa.invitationToken}>Accept invitation</button>
            </div>
        } else if (accessState === "LEFT") {
            return '';
        } else if (accessState === "GRANTED") {
            if (isPermissionAllowedForWorkspace(userContext.userProfile, wa.workspaceId, "manage_workspace")) {
                return <div className="text-right">
                    <Link to={`/account/workspace/manage?wid=${wa.workspaceId}`}
                          className="btn btn-xs btn-outline-primary ml-2"><span className="fa fa-pen mr-1"/>Manage</Link>
                    <Link to={`/account/workspace/notifications?aid=${wa.accessId}`}
                          className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-bell mr-1"/>Notifications</Link>
                </div>
            } else {
                return <div className="text-right">
                    <button onClick={onLeave} className="btn btn-xs btn-outline-danger ml-2"><span className="fa fa-trash-alt mr-1"/>Leave</button>
                    <Link to={`/account/workspace/notifications?aid=${wa.accessId}`}
                          className="btn btn-xs btn-outline-gray-dark ml-2"><span className="fa fa-pen mr-1"/>Notifications</Link>
                </div>
            }

        }
    }

    // ==========================================================================================================

    renderCreateModal(userContext) {

        const { showCreateModal, newWorkspaceName} = this.state;

        const handleClose = () => this.setState({ showCreateModal: false, newWorkspaceName: "" });

        const handleChange = (evt) => this.setState({ newWorkspaceName: evt.target.value});

        const handleCreate = () => {
            const { newWorkspaceName } = this.state;
            if (!newWorkspaceName || newWorkspaceName.length <= 0) {
                return;
            }

            const payload = {
                name: newWorkspaceName
            };

            return post("/rest/workspace", payload)
                .then((newAccess) => {
                    successToast(`Workspace ${newAccess.name} created.`);
                    // Get the appropriate permissions for the newly-created workspace
                    this.fetchProfile()
                        .then(newProfile => {
                            if (newProfile && newProfile.id && newProfile.accountType) {
                                const newContext = Object.assign({}, this.context);
                                newContext.userProfile = newProfile;
                                newContext.userContextUpdater(newContext);
                            }
                            return this.fetchWorkspaceAccessList();
                        })
                })
                .catch((err) => {
                        failedToast("Something went wrong while creating a workspace");
                        if (err && err.code === 401) {
                            let newContext = handleErrorFetch(userContext);
                            userContext.userContextUpdater(newContext);
                        }
                        console.log('Error in unlink connection: ', err)
                    }
                )
                .finally(() => this.setState({showCreateModal: false, newWorkspaceName: ""}))
        }

        return (
            <div>
                <div className={`modal-backdrop fade ${showCreateModal ? "show d-block" : "d-none"}`}></div>
                <div className={`modal fade ${showCreateModal ? "show d-block" : "d-none"}`} role="dialog" tabIndex="-1"
                     aria-labelledby="Delete_Modal_label" aria-hidden={!showCreateModal} aria-modal={showCreateModal} >
                    <div className="modal-dialog modal-md" role="document">
                        <div className="modal-content">
                            <div id="Delete_Modal_label" className="modal-header p-3">
                                <h4 className="modal-title">Create a new Workspace</h4>
                                <button type="button" className="close" onClick={handleClose} data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <h6 className="">Name</h6>
                                <div className="input-group input-group-sm">
                                    <input type="text" value={newWorkspaceName} onChange={handleChange}
                                           placeholder={"Assign a name to this workspace"}
                                           maxLength={80}
                                           className={"form-control " + (newWorkspaceName ? "" : "is-invalid")} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-outline-secondary btn-xs mr-2"
                                            onClick={() => this.setState({ showCreateModal: false })}>Dismiss
                                    </button>
                                    <button className="btn btn-primary btn-xs"
                                            onClick={handleCreate} disabled={!newWorkspaceName || newWorkspaceName.length === 0} >Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

    }
}

export default withRouter(WorkspaceDashboard)